
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

interface ClubModeContextValue {
  isClubMode: boolean;
  setClubMode: (on: boolean) => void;
  toggleClubMode: () => void;
}

const ClubModeContext = createContext<ClubModeContextValue | undefined>(undefined);

const AUTO_ON_PREFIXES = [
  "/security",
  "/security-check-in",
  "/club-entry",
  "/dashboard/security",
];

export const ClubModeProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [isClubMode, setIsClubMode] = useState<boolean>(() => {
    const saved = localStorage.getItem("noche_club_mode");
    if (saved != null) return saved === "true";
    return AUTO_ON_PREFIXES.some((p) => location.pathname.startsWith(p));
  });

  // Persist and set body class for high-contrast, large touch targets
  useEffect(() => {
    localStorage.setItem("noche_club_mode", String(isClubMode));
    const body = document.body;
    if (isClubMode) body.classList.add("club-mode");
    else body.classList.remove("club-mode");
  }, [isClubMode]);

  // Auto-enable on specific routes (does not override manual toggle off in same session)
  useEffect(() => {
    if (AUTO_ON_PREFIXES.some((p) => location.pathname.startsWith(p))) {
      setIsClubMode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const value = useMemo(
    () => ({
      isClubMode,
      setClubMode: setIsClubMode,
      toggleClubMode: () => setIsClubMode((v) => !v),
    }),
    [isClubMode]
  );

  return <ClubModeContext.Provider value={value}>{children}</ClubModeContext.Provider>;
};

export const useClubMode = () => {
  const ctx = useContext(ClubModeContext);
  if (!ctx) {
    if (typeof window !== "undefined") {
      console.warn("useClubMode used outside ClubModeProvider - falling back to defaults");
    }
    return {
      isClubMode: false,
      setClubMode: () => {},
      toggleClubMode: () => {},
    } as ClubModeContextValue;
  }
  return ctx;
};
